import { useMutation, useQueryClient } from "@tanstack/react-query";
import { actOnRfq, raiseRfqQuery } from "../../../services/rfqService";
import type { RaiseRfqQueryPayload, RfqActionPayload, RfqInvitationId } from "../types";
import { rfqApiError } from "../utils/apiError";
import { rfqKeys } from "./queryKeys";

type WithId<T> = T & { id: RfqInvitationId };

const REFRESH_ON = new Set(["VERSION_CONFLICT", "ACTION_NOT_ALLOWED"]);

export function useRfqAction() {
  const queryClient = useQueryClient();
  const refresh = (id: RfqInvitationId) => {
    queryClient.invalidateQueries(rfqKeys.detail(id));
    queryClient.invalidateQueries(rfqKeys.lists());
  };
  return useMutation(
    ({ id, ...payload }: WithId<RfqActionPayload>) => actOnRfq(id, payload),
    {
      onSuccess: (data, { id }) => {
        queryClient.setQueryData(rfqKeys.detail(id), data);
        refresh(id);
      },
      onError: (error: unknown, { id }) => {
        const code = rfqApiError(error)?.error_code;
        if (code && REFRESH_ON.has(code)) refresh(id);
      },
    },
  );
}

export function useRaiseRfqQuery() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, ...payload }: WithId<RaiseRfqQueryPayload>) => raiseRfqQuery(id, payload),
    {
      onSuccess: (_data, { id }) => {
        queryClient.invalidateQueries(rfqKeys.detail(id));
      },
    },
  );
}
